import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Multiplies product price and image-tile price diffs by the selected quantity.
 * Hooks into `variant:update` and `quantity-selector:update` — no extra network
 * requests needed.
 *
 * Requires `product-price[data-unit-price]` to be present in the section.
 * Data attributes set by blocks/price.liquid:
 *   - data-unit-price        — variant price in minor units (cents)
 *   - data-unit-compare-price — compare-at price in minor units (0 if none)
 *   - data-money-format       — shop.money_format stripped of HTML
 *   - data-currency           — ISO currency code (e.g. "EUR")
 *
 * Image tile price diffs require:
 *   - data-unit-diff on .variant-option__image-tile-price spans (in minor units)
 */
class PriceQtyMultiplier {
  /** @type {Element} */
  #section;
  /** @type {number} */
  #qty = 1;

  /** @param {Element} section */
  constructor(section) {
    this.#section = section;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQtyUpdate);
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    // Sync unit-price data attrs from the new HTML onto the live element.
    // product-price.js only replaces [ref="priceContainer"], not the outer
    // product-price element, so we update the data attrs manually.
    const newEl = event.detail?.data?.html?.querySelector('product-price[data-unit-price]');
    const liveEl = this.#section.querySelector('product-price[data-unit-price]');
    if (newEl && liveEl) {
      liveEl.dataset.unitPrice = newEl.dataset.unitPrice;
      liveEl.dataset.unitComparePrice = newEl.dataset.unitComparePrice ?? '0';
    }
    // product-price.js replaces [ref="priceContainer"] in its own synchronous
    // event handler. Use queueMicrotask so we run after that replacement and
    // overwrite with the qty-multiplied value.
    queueMicrotask(() => this.#apply());
  };

  /** @param {import('@theme/events').QuantitySelectorUpdateEvent} event */
  #onQtyUpdate = (event) => {
    this.#qty = event.detail?.quantity ?? 1;
    this.#apply();
  };

  #apply() {
    const priceEl = this.#section.querySelector('product-price[data-unit-price]');
    if (!priceEl) return;

    const unitPrice = parseInt(priceEl.dataset.unitPrice, 10);
    const unitCompare = parseInt(priceEl.dataset.unitComparePrice ?? '0', 10);
    const moneyFormat = priceEl.dataset.moneyFormat;
    const currency = priceEl.dataset.currency ?? 'EUR';

    if (!unitPrice || !moneyFormat) return;

    const formatted = formatMoney(unitPrice * this.#qty, moneyFormat, currency);

    // Update all .price spans inside product-price
    priceEl.querySelectorAll('.price').forEach((el) => {
      el.textContent = formatted;
    });

    // Update compare-at price spans (only if there is a compare price)
    if (unitCompare > 0) {
      const formattedCompare = formatMoney(unitCompare * this.#qty, moneyFormat, currency);
      priceEl.querySelectorAll('.compare-at-price').forEach((el) => {
        el.textContent = formattedCompare;
      });
    }

    // Update image-tile price diffs
    this.#section
      .querySelectorAll('.variant-option__image-tile-price[data-unit-diff]')
      .forEach((el) => {
        const diff = parseInt(el.dataset.unitDiff, 10);
        if (!diff) return;
        el.textContent = '+' + formatMoney(diff * this.#qty, moneyFormat, currency);
      });
  }
}

// Auto-init for every section (product page) that has a price block with unit-price data.
// type="module" guarantees this script runs at most once per page load.
for (const section of document.querySelectorAll('.shopify-section, dialog')) {
  if (section.querySelector('product-price[data-unit-price]')) {
    new PriceQtyMultiplier(section);
  }
}
